<template>
  <nuttrema-layout-form-standard
     theme="west-top"
    :edit="true"
    :saveError="PersonProgram.saveError">

    <template slot="center">

      <el-form
         label-position="top"
        :ref="formRef"
        :model="formData"
        :rules="rules"
        :validate-on-rule-change="false">

        <section>
          <h4 class="section-title">{{ $t('label.participant') }}</h4>
          <div class="section-items is-info-card">
            <user-info-small-card
               class="is-in-section"
              :reactive="false"
              :personDetails="PersonDetails.data">
            </user-info-small-card>
          </div>
        </section>

        <section>
          <h4 class="section-title">{{ $t('label.main_information') }}</h4>
          <div class="section-items">

            <el-form-item
               prop="program_type_code"
              :label="$t('label.program_type')">
              <el-select
                 v-model="formData.program_type_code">
                <el-option
                   v-for="program_type in formData.program_type_codes"
                  :key="program_type.code"
                  :label="$t(program_type.label)"
                  :disabled="program_type.disabled"
                  :value="program_type.code">
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item
               prop="name"
              :label="$t('label.name')">
              <el-col :span="24">
                <el-input
                  v-model="formData.name"
                  class="w-100">
                </el-input>
              </el-col>
            </el-form-item>

            <el-form-item
               prop="start_date"
               class="el-form-item__label--is-description"
              :label="$t('label.date')">
              <template slot="label">
                {{ $t('label.date') }} <span v-if="duration > 0">{{ $tc('label.days_number', duration, { duration }) }}</span>
              </template>
              <el-col :span="24">
                <el-date-picker
                   v-model="formData.start_date"
                   class="w-100"
                   type="daterange"
                   format="dd/MM/yyyy"
                   value-format="yyyy-MM-dd"
                  :range-separator="$t('label.to')"
                  :start-placeholder="$t('label.start_date')"
                  :end-placeholder="$t('label.end_date')"
                  :picker-options="options.start_date">
                </el-date-picker>
              </el-col>
            </el-form-item>

            <div class="section-help">
              <p>{{ $t('label.maximum_program_duration', { upper_limit: _get(InstanceSubscribedPlanSummarized, 'data.subscribed_plan.program_duration.upper_limit') }) }}</p>
            </div>

          </div>
          <!-- end ~ section - main_information -->

        </section>

      </el-form>

    </template>

    <template slot="footer">
      <div class="footer-row">
        <el-button type="primary" size="small" :loading="isSaving" @click="submitForm">{{ $t('label.create') }}</el-button>
        <el-button type="text" size="small" @click="handleCancelClick">{{ $t('label.cancel') }}</el-button>
      </div>
    </template>

  </nuttrema-layout-form-standard>
</template>

<script>
import moment from 'moment';
import { includes, get } from 'lodash';

import { PROGRAMS } from 'constants/types';

import NuttremaLayoutFormStandard from 'components/ui/layout/nuttrema-layout-form-standard';
import FormStandardBehavior from 'src/components/mixins/form-standard-behavior';

import UserInfoSmallCard from 'components/ui/card/user-info-small-card';

export default {
  name: 'form-create-assigned-program',
  mixins: [
    FormStandardBehavior,
  ],
  props: {
    id: {
      required: true,
      default: null,
    },
    PersonDetails: {
      required: true,
      default: null,
    },
    PersonOccupiedDates: {
      required: true,
      default: null,
    },
    PersonProgram: {
      required: true,
      default: null,
    },
    InstanceSubscribedPlanSummarized: {
      required: false,
      default: null,
    },
  },
  components: {
    UserInfoSmallCard,
    NuttremaLayoutFormStandard,
  },
  data() {
    return {
      options: {
        start_date: {
          disabledDate: this.disabledDate,
        },
      },
      formData: {
        duration: 1,
        name: null,
        start_date: null,
        price: null,
        currency: null,
        payment_status: false,
        program_type_codes: [
          {
            code: PROGRAMS.DIET,
            label: 'label.diet',
            disabled: false,
          },
          {
            code: PROGRAMS.WORKOUT,
            label: 'label.workouts',
            disabled: true,
          },
        ],
        program_type_code: 'diet',
      },
      rules: {
        name: [
          {
            required: true,
            trigger: 'blur',
            message: this.formatValidationMessage('validation.required', 'label.name', true),
          },
        ],
        start_date: [
          {
            len: 2,
            type: 'array',
            required: true,
            trigger: 'blur',
            message: this.formatValidationMessage('validation.required', 'label.start_and_end_date', true),
            fields: {
              0: { required: true, message: this.formatValidationMessage('validation.required', 'label.start_date', true) },
              1: { required: true, message: this.formatValidationMessage('validation.required', 'label.end_date', true) },
            },
          },
          {
            trigger: 'blur',
            validator: this.validateDuration,
            message: this.formatValidationMessage('validation.program_duration_max_limit'),
          },
        ],
      },
    };
  },
  computed: {
    duration() {
      const startDate = get(this.formData, 'start_date.0', false);
      const endDate = get(this.formData, 'start_date.1', false);
      return startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') + 1 : 0;
    },
  },
  methods: {
    validateDuration(rule, value, callback) {
      const max = get(this.InstanceSubscribedPlanSummarized, 'data.subscribed_plan.program_duration.upper_limit');
      const isValid = Number(this.duration) <= Number(max);
      if (isValid) return callback();
      return callback(new Error());
    },
    disabledDate(time) {
      const inPast = time.getTime() < moment().subtract(1, 'day').toDate();
      const occupiedDates = get(this.PersonOccupiedDates, 'data', []);
      const wasOccupied = includes(occupiedDates, moment(time).format('YYYY-MM-DD'));
      return inPast || wasOccupied;
    },
    getFormData() {
      const startDate = get(this.formData, 'start_date.0', null);
      const data = {
        program_type_code: this.formData.program_type_code,
        start_date: startDate,
        price: this.formData.price,
        currency: this.formData.currency,
        name: this.formData.name,
        duration: this.duration,
      };

      return this.removeEmpty(data);
    },
    handleCancelClick() {
      this.$emit('back');
    },
  },
  created() {
    this.stateData = this.PersonProgram;
    this.formRef = 'form-create-assigned-program';
  },
};
</script>



// WEBPACK FOOTER //
// src/components/pages/professional/programs/shared/create/form-create-assigned-program.vue